import { Link, useSearchParams } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { useOrders } from '@/hooks/useOrders'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Package, ArrowRight, Loader2 } from 'lucide-react'
import { format } from 'date-fns'
import type { FulfillmentStatus } from '@/types/database'
import { getPageTitle } from '@/lib/branding'

const statusColors: Record<FulfillmentStatus, string> = {
  pending: 'bg-yellow-100 text-yellow-800',
  processing: 'bg-blue-100 text-blue-800',
  shipped: 'bg-purple-100 text-purple-800',
  delivered: 'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-800',
}

const statusFilters: { label: string; value: FulfillmentStatus | 'all' }[] = [
  { label: 'All', value: 'all' },
  { label: 'Processing', value: 'processing' },
  { label: 'Shipped', value: 'shipped' },
  { label: 'Delivered', value: 'delivered' },
]

export function AccountOrdersPage() {
  const [searchParams] = useSearchParams()
  const statusFilter = (searchParams.get('status') as FulfillmentStatus | 'all') || 'all'
  const { orders, isLoading } = useOrders(statusFilter)

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return (
    <>
      <Helmet>
        <title>{getPageTitle('Order History')}</title>
      </Helmet>
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold">Order History</h1>
          <p className="text-muted-foreground mt-1">
            View and track all your orders
          </p>
        </div>

        {/* Filters */}
        <div className="flex gap-2 overflow-x-auto pb-2">
          {statusFilters.map((filter) => (
            <Link
              key={filter.value}
              to={filter.value === 'all' ? '/account/orders' : `/account/orders?status=${filter.value}`}
            >
              <Button
                variant={statusFilter === filter.value ? 'default' : 'outline'}
                size="sm"
              >
                {filter.label}
              </Button>
            </Link>
          ))}
        </div>

        {/* Orders List */}
        <Card>
          <CardHeader>
            <CardTitle>Your Orders</CardTitle>
            <CardDescription>
              {orders.length} order{orders.length !== 1 ? 's' : ''} found
            </CardDescription>
          </CardHeader>
          <CardContent>
            {orders.length > 0 ? (
              <div className="space-y-4">
                {orders.map((order) => (
                  <Link
                    key={order.id}
                    to={`/account/orders/${order.id}`}
                    className="flex items-center gap-4 p-4 rounded-lg border hover:bg-muted/50 transition-colors"
                  >
                    {/* Thumbnail */}
                    <div className="h-16 w-16 rounded-md bg-muted flex items-center justify-center overflow-hidden flex-shrink-0">
                      {(order.product?.hero_image_url || order.product?.video_thumbnail_url) ? (
                        <img
                          src={order.product.hero_image_url || order.product.video_thumbnail_url!}
                          alt={order.product.title || 'Product'}
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <Package className="h-6 w-6 text-muted-foreground" />
                      )}
                    </div>

                    {/* Order Details */}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">
                        {order.product?.title || 'Product'}
                        {(order.item_count ?? 0) > 1 && (
                          <span className="text-muted-foreground font-normal">
                            {' '}+ {(order.item_count ?? 0) - 1} more
                          </span>
                        )}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Order #{order.id.slice(0, 8)}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {format(new Date(order.created_at), 'MMM d, yyyy')}
                      </p>
                    </div>

                    {/* Status & Price */}
                    <div className="text-right flex-shrink-0">
                      <Badge className={statusColors[order.fulfillment_status]}>
                        {order.fulfillment_status}
                      </Badge>
                      <p className="text-sm font-medium mt-1">
                        ${Number(order.total_amount).toFixed(2)}
                      </p>
                    </div>

                    <ArrowRight className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Package className="mx-auto h-12 w-12 text-muted-foreground" />
                <h3 className="mt-4 text-lg font-semibold">No orders found</h3>
                <p className="text-muted-foreground mt-1">
                  {statusFilter !== 'all'
                    ? `You don't have any ${statusFilter} orders`
                    : 'When you make a purchase, it will appear here'}
                </p>
                <Link to="/">
                  <Button className="mt-4">Browse Products</Button>
                </Link>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  )
}
